import { useCallback, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useOutletApi } from "@/features/authentication";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomAsyncAutocomplete,
  ProductVariantNameCell,
  useSnackbar,
} from "@/features/ui";
import { ReturnableTypeEnum } from "../../returnable-type";
import {
  PurchaseReturnStatusEnum,
  StatusEnum,
} from "@/features/goods-receipts";

export const SelectGoodsReceiptProductVariantSection = () => {
  const apiRef = useGridApiRef();
  const { control, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();
  const [goodsReceipt, setGoodsReceipt] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedGoodsReceiptProductVariants,
    setSelectedGoodsReceiptProductVariants,
  ] = useState([]);

  const { append } = useFieldArray({
    control,
    name: "returnableProductVariants",
  });

  const [seller, buyer, returnableProductVariants, returnableId] = useWatch({
    control,
    name: ["seller", "buyer", "returnableProductVariants", "returnableId"],
    exact: true,
  });

  const outletApi = useOutletApi(buyer?.id);

  // Disable if seller not selected OR if items exist from different source
  const disableBrowseGoodsReceipt =
    !seller || (returnableProductVariants?.length > 0 && returnableId !== null);

  const fetchGoodsReceipts = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!seller || !buyer) return [];

      const returnableStatuses = [
        PurchaseReturnStatusEnum.NONE,
        PurchaseReturnStatusEnum.PARTIALLY_RETURNED,
      ];

      const invoiceStatuses = [
        StatusEnum.RECEIVED,
        StatusEnum.PARTIALLY_INVOICED,
      ].join(",");

      const {
        data: { data },
      } = await outletApi.get("goods-receipts", {
        params: {
          page,
          size,
          sellerableId: seller?.id,
          referenceNumber: searchValue || null,
          returnStatuses: returnableStatuses,
          status: invoiceStatuses,
        },
      });
      return data;
    },
    [buyer, seller, outletApi]
  );

  const fetchGoodsReceiptProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !buyer) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `goods-receipts/${referenceNumber}/goods-receipt-product-variants`,
          {
            params: {
              withPurchaseOrderProductVariant: true,
              excludeFullyInvoiced: true,
              excludeFullyReturned: true,
            },
          }
        );

        // Adjust remaining quantity based on existing return items
        const adjustedProductVariants = data
          .map((goodsReceiptProductVariant) => {
            const totalReturnedQuantityInCurrentForm = returnableProductVariants
              .filter(
                (returnProductVariant) =>
                  returnProductVariant.returnableProductVariantId ===
                  goodsReceiptProductVariant.id
              )
              .reduce((total, variant) => total + variant.quantity, 0);

            return {
              ...goodsReceiptProductVariant,
              adjustedRemainingQuantity:
                goodsReceiptProductVariant.quantity -
                goodsReceiptProductVariant.returnedQuantity -
                goodsReceiptProductVariant.invoicedQuantity -
                totalReturnedQuantityInCurrentForm,
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0);

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        showSnackbar(getErrorMessage(error, "Gagal memuat rincian penerimaan"), "error");
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [buyer, returnableProductVariants, outletApi, showSnackbar]
  );

  const handleGoodsReceiptChange = (newValue) => {
    setGoodsReceipt(newValue);
    if (newValue) {
      fetchGoodsReceiptProductVariants(newValue.referenceNumber);
      setDialogOpen(true);
    } else {
      setProductVariants([]);
    }
    setSelectedGoodsReceiptProductVariants([]);
  };

  const columns = [
    {
      field: "name",
      headerName: "Nama Barang",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Kuantitas",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Satuan",
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
  ];

  const submitSelection = () => {
    // Check if there's already a returnable source set
    if (returnableId && returnableId !== goodsReceipt.id) {
      showSnackbar(
        "Anda hanya dapat memilih produk dari satu sumber dokumen. Hapus produk yang ada terlebih dahulu.",
        "error"
      );
      return;
    }

    // Set the returnable source if not already set
    if (!returnableId) {
      setValue("returnableId", goodsReceipt.id);
      setValue("returnableType", ReturnableTypeEnum.GOODS_RECEIPT);
    }

    selectedGoodsReceiptProductVariants.forEach(
      ({
        id: goodsReceiptProductVariantId,
        name,
        sku,
        adjustedRemainingQuantity,
        product: {
          unitOfMeasurement: { name: unitOfMeasurement },
        },
        purchaseOrderProductVariant,
      }) => {
        const alreadyExists = returnableProductVariants.some(
          (variant) =>
            variant.returnableProductVariantId === goodsReceiptProductVariantId
        );

        if (!alreadyExists) {
          append({
            id: ulid(),
            returnableProductVariantId: goodsReceiptProductVariantId,
            warehouseId: goodsReceipt.warehouseId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            maxQuantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            price: purchaseOrderProductVariant?.costPerUnit ?? 0,
            discount: 0,
            note: null,
          });
        }
      }
    );

    // Close dialog
    setDialogOpen(false);
    setProductVariants([]);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setProductVariants([]);
  };

  return (
    <>
      <CustomAsyncAutocomplete
        value={goodsReceipt}
        onChange={handleGoodsReceiptChange}
        disabled={disableBrowseGoodsReceipt}
        fetchOptions={fetchGoodsReceipts}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const { id, referenceNumber, transactionDate } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>{referenceNumber}</div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowseGoodsReceipt
            ? !seller
              ? "Pilih Pemasok terlebih dahulu"
              : "Tidak dapat mengubah sumber dokumen setelah menambah produk"
            : undefined,
          InputProps: {
            placeholder: "Cari No. Referensi Penerimaan",
          },
        }}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Rincian Barang
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DataGrid
            getRowHeight={() => "auto"}
            apiRef={apiRef}
            columns={columns}
            rows={productVariants}
            loading={loadingProductVariants}
            checkboxSelection
            onRowSelectionModelChange={() => {
              const selectedModels = Array.from(
                gridRowSelectionIdsSelector(apiRef.current.state).values()
              );
              setSelectedGoodsReceiptProductVariants(selectedModels);
            }}
            sx={{ minHeight: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            fullWidth
            variant="contained"
            disabled={selectedGoodsReceiptProductVariants.length === 0}
            onClick={submitSelection}
          >
            Lanjut
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
